import React, { useEffect, useState } from 'react';
import { Download, XCircle, Plus, Filter } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Card';
import { Input, Textarea, Field } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { formatINR, formatDate, slotLabel } from '@/lib/utils';
import { AdminCreateBookingModal } from '@/components/admin/AdminCreateModals';

export default function AdminBookings() {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ start: '', end: '', status: '' });
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelBusy, setCancelBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
      const { data } = await api.get('/admin/reports/bookings', { params });
      setRows(data);
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const exportCSV = () => {
    const headers = ['Booking ID', 'Customer', 'Email', 'Phone', 'Court', 'Date', 'Time', 'Amount', 'Status', 'Payment'];
    const lines = rows.map((b) => [
      b.id.slice(0, 8), b.customer_name, b.customer_email, b.customer_phone, b.court_name,
      b.booking_date, slotLabel(b.start_hour, b.duration_hours), b.amount, b.status, b.payment_status,
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const blob = new Blob([[headers.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `kba-bookings-${Date.now()}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="admin-bookings">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-2">Audit</div>
          <h1 className="font-heading font-black text-4xl uppercase tracking-tight">Bookings Report</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCSV} data-testid="export-bookings"><Download size={14} /> Export CSV</Button>
          <Button onClick={() => setShowCreate(true)} data-testid="new-booking-button"><Plus size={14} /> New Booking</Button>
        </div>
      </div>

      <div className="bg-surface border border-white/10 p-5 grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Field label="From"><Input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} data-testid="filter-start" /></Field>
        <Field label="To"><Input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} data-testid="filter-end" /></Field>
        <Field label="Status">
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="w-full h-11 px-4 bg-transparent border border-white/20 text-white focus:border-volt focus:outline-none" data-testid="filter-status">
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </Field>
        <div className="flex items-end"><Button onClick={load} className="w-full" data-testid="apply-filters"><Filter size={14} /> Apply</Button></div>
      </div>

      <div className="bg-surface border border-white/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]">
          <thead className="border-b border-white/10 bg-obsidian/40">
            <tr className="text-[10px] uppercase tracking-[0.25em] text-white/40">
              <th className="text-left px-5 py-3">ID</th>
              <th className="text-left px-5 py-3">Customer</th>
              <th className="text-left px-5 py-3">Court</th>
              <th className="text-left px-5 py-3">Date</th>
              <th className="text-left px-5 py-3">Time</th>
              <th className="text-right px-5 py-3">Amount</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-left px-5 py-3">Payment</th>
              <th className="text-right px-5 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="9" className="px-5 py-12 text-center text-white/40">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="9" className="px-5 py-12 text-center text-white/40">No bookings.</td></tr>
            ) : rows.map((b) => (
              <tr key={b.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`booking-row-${b.id}`}>
                <td className="px-5 py-3 font-mono text-xs">{b.id.slice(0, 8)}</td>
                <td className="px-5 py-3">
                  <div className="font-medium">{b.customer_name}</div>
                  <div className="text-[10px] text-white/40 uppercase tracking-wider">{b.customer_email}</div>
                </td>
                <td className="px-5 py-3 text-white/70">{b.court_name}</td>
                <td className="px-5 py-3 text-white/70">{formatDate(b.booking_date)}</td>
                <td className="px-5 py-3 font-mono text-xs text-white/70">{slotLabel(b.start_hour, b.duration_hours)}</td>
                <td className="px-5 py-3 text-right font-mono text-volt">{formatINR(b.amount)}</td>
                <td className="px-5 py-3"><Badge tone={b.status === 'cancelled' ? 'danger' : b.status === 'confirmed' ? 'success' : 'warning'}>{b.status}</Badge></td>
                <td className="px-5 py-3"><Badge tone={b.payment_status === 'paid' ? 'success' : b.payment_status === 'refunded' ? 'default' : 'warning'}>{b.payment_status}</Badge></td>
                <td className="px-5 py-3 text-right">
                  {b.status !== 'cancelled' ? (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => { setCancelTarget(b); setCancelReason(''); }}
                      data-testid={`cancel-booking-${b.id}`}
                    >
                      <XCircle size={12} /> {b.payment_status === 'paid' ? 'Cancel + Refund' : 'Cancel'}
                    </Button>
                  ) : (
                    <span className="text-[10px] uppercase tracking-[0.25em] text-white/40">
                      {b.refund_amount ? `Refunded ${formatINR(b.refund_amount)}` : '—'}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {cancelTarget && (
        <CancelBookingModal
          booking={cancelTarget}
          reason={cancelReason}
          setReason={setCancelReason}
          busy={cancelBusy}
          onClose={() => setCancelTarget(null)}
          onConfirm={async () => {
            setCancelBusy(true);
            try {
              const { data } = await api.post(`/admin/bookings/${cancelTarget.id}/cancel`, { reason: cancelReason });
              toast({
                title: data.refunded ? `Refund issued (${data.refund_mode || ''})` : 'Booking cancelled',
                description: data.refunded ? `${formatINR(data.refund_amount)} → customer` : 'Customer notified',
                variant: 'success',
              });
              setCancelTarget(null);
              setCancelReason('');
              load();
            } catch (e) {
              toast({ title: 'Cancel failed', description: e.response?.data?.detail || '', variant: 'error' });
            } finally { setCancelBusy(false); }
          }}
        />
      )}

      {showCreate && (
        <AdminCreateBookingModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}

function CancelBookingModal({ booking, reason, setReason, busy, onClose, onConfirm }) {
  const paid = booking.payment_status === 'paid';
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface border border-white/10 max-w-lg w-full p-6 sm:p-8" onClick={(e) => e.stopPropagation()} data-testid="cancel-booking-modal">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading font-bold text-2xl uppercase">Cancel Booking{paid ? ' + Refund' : ''}</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white"><XCircle size={18} /></button>
        </div>
        <div className="space-y-3 text-sm text-white/80 bg-obsidian border border-white/10 p-5 mb-5">
          <div><span className="text-white/40 uppercase text-[10px] tracking-[0.25em] mr-2">Customer</span>{booking.customer_name}</div>
          <div><span className="text-white/40 uppercase text-[10px] tracking-[0.25em] mr-2">Court</span>{booking.court_name}</div>
          <div><span className="text-white/40 uppercase text-[10px] tracking-[0.25em] mr-2">When</span>{formatDate(booking.booking_date)} · {slotLabel(booking.start_hour, booking.duration_hours)}</div>
          <div><span className="text-white/40 uppercase text-[10px] tracking-[0.25em] mr-2">Amount</span><span className="text-volt font-mono">{formatINR(booking.amount)}</span></div>
        </div>
        {paid ? (
          <p className="text-xs text-volt mb-4">A refund of <span className="font-mono">{formatINR(booking.amount)}</span> will be issued to the customer's original payment method via Razorpay.</p>
        ) : (
          <p className="text-xs text-white/60 mb-4">This booking hasn't been paid yet — no refund required.</p>
        )}
        <Field label="Reason (optional — sent in WhatsApp message)">
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Court maintenance scheduled / customer request / weather …" data-testid="cancel-reason" />
        </Field>
        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={onClose} className="flex-1">Keep Booking</Button>
          <Button variant="danger" onClick={onConfirm} disabled={busy} className="flex-1" data-testid="confirm-cancel">
            {busy ? 'Processing…' : paid ? <><XCircle size={14} /> Cancel + Refund</> : <><XCircle size={14} /> Cancel Booking</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
